import React, { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import styled from 'styled-components';
import L from 'leaflet';

const interpolateValue = (
  data: number[],
  header: any,
  lat: number,
  lng: number
): { u: number; v: number } | null => {
  const { nx, ny, lo1, la1, dx, dy } = header;
  
  const normLng = lng < 0 ? lng + 360 : lng;
  
  const x = (normLng - lo1) / dx;
  const y = (la1 - lat) / dy;
  
  if (x < 0 || x >= nx - 1 || y < 0 || y >= ny - 1) {
    return null;
  }
  
  const x0 = Math.floor(x);
  const x1 = x0 + 1;
  const y0 = Math.floor(y);
  const y1 = y0 + 1;
  const fx = x - x0;
  const fy = y - y0;
  
  const getUV = (xi: number, yi: number) => {
    const idx = (yi * nx + xi) * 2;
    return {
      u: data[idx] || 0,
      v: data[idx + 1] || 0,
    };
  };
  
  const p00 = getUV(x0, y0);
  const p10 = getUV(x1, y0);
  const p01 = getUV(x0, y1);
  const p11 = getUV(x1, y1);
  
  const u =
    p00.u * (1 - fx) * (1 - fy) +
    p10.u * fx * (1 - fy) +
    p01.u * (1 - fx) * fy +
    p11.u * fx * fy;
    
  const v =
    p00.v * (1 - fx) * (1 - fy) +
    p10.v * fx * (1 - fy) +
    p01.v * (1 - fx) * fy +
    p11.v * fx * fy;
  
  return { u, v };
};

const calculateMetrics = (u: number, v: number) => {
  const speed = Math.sqrt(u * u + v * v);
  let direction = (Math.atan2(u, v) * 180) / Math.PI;
  direction = (direction + 360) % 360; 
  
  const cardinals = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const cardinalIndex = Math.round(direction / 45) % 8;
  const cardinal = cardinals[cardinalIndex];
  
  return { speed, direction, cardinal };
};

interface VelocityTooltipProps {
  showWindLayer: boolean;
  showWaveLayer: boolean;
  windDataRef: React.MutableRefObject<any>;
  waveDataRef: React.MutableRefObject<any>;
  isWindDataReady: boolean;
  isWaveDataReady: boolean;
}

const VelocityTooltip: React.FC<VelocityTooltipProps> = ({
  showWindLayer,
  showWaveLayer,
  windDataRef,
  waveDataRef,
  isWindDataReady,
  isWaveDataReady,
}) => {
  const map = useMap();
  
  const [tooltipData, setTooltipData] = useState<{
    x: number;
    y: number;
    visible: boolean;
    type: 'wind' | 'wave' | null;
    speed: number;
    direction: number;
    cardinal: string;
    height?: number;
  }>({
    x: 0,
    y: 0,
    visible: false,
    type: null,
    speed: 0,
    direction: 0,
    cardinal: 'N',
  });

  useEffect(() => {
    if (!map) return;
    
    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      try {
        if (!showWindLayer && !showWaveLayer) {
          setTooltipData(prev => ({ ...prev, visible: false }));
          return;
        }
        
        if (!map.getContainer()) {
          return;
        }
        
        const originalEvent = (e as any).originalEvent;
        if (originalEvent) {
          const target = originalEvent.target as HTMLElement;
          let element: HTMLElement | null = target;
          while (element && element !== map.getContainer()) {
            const zIndex = window.getComputedStyle(element).zIndex;
            if (zIndex && parseInt(zIndex) >= 990) {
              setTooltipData(prev => ({ ...prev, visible: false }));
              return;
            }
            element = element.parentElement;
          }
        }
        
        const waveDataReady = showWaveLayer && isWaveDataReady && waveDataRef.current;
        const windDataReady = showWindLayer && isWindDataReady && windDataRef.current;
        
        if (!waveDataReady && !windDataReady) {
          setTooltipData(prev => ({ ...prev, visible: false }));
          return;
        }
        
        const { lat, lng } = e.latlng;
        let tooltipInfo: any = null;
        
        if (showWaveLayer && waveDataRef.current) {
          const { header, heightData, directionData } = waveDataRef.current;
          
          if (!header || !heightData || !directionData) {
            return;
          }
          
          const { nx, ny, lo1, la1, dx, dy } = header;
          
          const normLng = lng < 0 ? lng + 360 : lng;
          
          const x = (normLng - lo1) / dx;
          const y = (la1 - lat) / dy;
          
          if (x >= 0 && x < nx && y >= 0 && y < ny) {
            const xi = Math.min(Math.round(x), nx - 1);
            const yi = Math.min(Math.round(y), ny - 1);
            const idx = yi * nx + xi;
            
            if (idx < 0 || idx >= heightData.length) {
              return;
            }
            
            let height = heightData[idx];
            let direction = directionData[idx];
            
            if (typeof height === 'string') height = parseFloat(height);
            if (typeof direction === 'string') direction = parseFloat(direction);
            if (isNaN(height) || height === null || height === undefined) height = 0;
            if (isNaN(direction) || direction === null || direction === undefined) direction = 0;
            
            if (height >= 0) {
              const radians = (direction * Math.PI) / 180;
              const metrics = calculateMetrics(
                -height * Math.sin(radians),
                -height * Math.cos(radians)
              );
              
              tooltipInfo = {
                type: 'wave',
                height: height,
                speed: metrics.speed,
                direction: direction,
                cardinal: metrics.cardinal,
              };
            }
          }
        } else if (showWindLayer && windDataRef.current) {
          if (!windDataRef.current.data || !windDataRef.current.header) {
            return;
          }
          
          const interpolated = interpolateValue(
            windDataRef.current.data,
            windDataRef.current.header,
            lat,
            lng
          );
          
          if (interpolated) {
            const metrics = calculateMetrics(interpolated.u, interpolated.v);
            
            if (metrics.speed >= 0) {
              tooltipInfo = {
                type: 'wind',
                speed: metrics.speed,
                direction: metrics.direction,
                cardinal: metrics.cardinal,
              };
            }
          }
        }
        
        if (tooltipInfo) {
          const containerPoint = map.latLngToContainerPoint(e.latlng);
          setTooltipData({
            x: containerPoint.x,
            y: containerPoint.y,
            visible: true,
            ...tooltipInfo,
          });
        } else {
          setTooltipData(prev => ({ ...prev, visible: false }));
        }
      } catch (error) {
      }
    };
    
    const timeoutId = setTimeout(() => {
      map.on('mousemove', handleMouseMove);
    }, 500);
    
    return () => {
      clearTimeout(timeoutId);
      map.off('mousemove', handleMouseMove);
    };
  }, [map, showWindLayer, showWaveLayer, windDataRef, waveDataRef, isWindDataReady, isWaveDataReady]);

  if (!tooltipData.visible) {
    return null;
  }

  return (
    <TooltipContainer
      style={{
        left: tooltipData.x + 15,
        top: tooltipData.y - 10,
      }}
    >
      {tooltipData.type === 'wave' ? (
        <>
          <TooltipLabel>Wave Height</TooltipLabel>
          <TooltipValue>{tooltipData.height?.toFixed(2)} m</TooltipValue>
          <TooltipDivider />
          <TooltipLabel>Direction</TooltipLabel>
          <TooltipValue>{tooltipData.direction?.toFixed(0)}° ({tooltipData.cardinal})</TooltipValue>
        </>
      ) : tooltipData.type === 'wind' ? (
        <>
          <TooltipLabel>Wind Speed</TooltipLabel>
          <TooltipValue>{tooltipData.speed?.toFixed(1)} m/s</TooltipValue>
          <TooltipDivider />
          <TooltipLabel>Direction</TooltipLabel>
          <TooltipValue>{tooltipData.direction?.toFixed(0)}° ({tooltipData.cardinal})</TooltipValue>
        </>
      ) : null}
    </TooltipContainer>
  );
};

export default VelocityTooltip;

const TooltipContainer = styled.div`
  position: absolute;
  background: rgba(255, 253, 253, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  pointer-events: none;
  z-index: 989;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 180px;
  transform: translateZ(0);
`;

const TooltipLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const TooltipValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #3a3a3aff;
  margin-bottom: 8px;
`;

const TooltipDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 8px 0;
`;
